import { gql, useQuery } from '@apollo/client';
import { Card } from '@apollo/orbit';
import _ from 'lodash';
import React from 'react';
import { Route, Switch, useLocation } from 'react-router';
import { Navigate } from 'react-router-dom-v5-compat';

import { Loading } from 'src/components/common/loading/Loading';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { DocumentTitle } from 'src/components/documentTitle/DocumentTitle';
import { ErrorPage } from 'src/components/errorPage/ErrorPage';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { BuildCheckDetails } from '../../checksPage/checkWorkflowTaskDetailsPage/buildCheckDetails/BuildCheckDetails';
import { CustomCheckDetails } from '../../checksPage/checkWorkflowTaskDetailsPage/customCheckDetails/CustomCheckDetails';
import { DownstreamCheckDetails } from '../../checksPage/checkWorkflowTaskDetailsPage/downstreamCheckDetails/DownstreamCheckDetails';
import { FilterBuildCheckDetails } from '../../checksPage/checkWorkflowTaskDetailsPage/filterBuildCheckDetails/FilterBuildCheckDetails';
import { LintCheckDetails } from '../../checksPage/checkWorkflowTaskDetailsPage/lintCheckDetails/LintCheckDetails';
import { OperationCheckResults } from '../../checksPage/checkWorkflowTaskDetailsPage/operationCheckResults/OperationCheckResults';
import {
  MatchingProposalsDrawer,
  MatchingProposalsDrawerFragment,
} from '../../checksPage/checkWorkflowTaskDetailsPage/proposalCheckDetails/MatchingProposalsDrawer';
import { ProposalCheckDetails } from '../../checksPage/checkWorkflowTaskDetailsPage/proposalCheckDetails/ProposalCheckDetails';
import { useInternalGraphLinking } from '../../hooks/useInternalGraphLinking';
import { CheckTaskType } from '../../routes';
import { CheckSidebar } from '../checkSidebar/CheckSidebar';
import { RerunBanner, RerunBannerCheckFragment } from '../RerunBanner';
import { useDefaultTaskType } from '../useDefaultTaskType';

import { TasksMenu, TasksMenuTaskFragment } from './TasksMenu';

const taskTypeDisplayValues = {
  build: 'Build',
  custom: 'Custom',
  downstream: 'Downstream',
  filter: 'Build',
  linter: 'Linter',
  operations: 'Operations',
  proposals: 'Proposals',
} satisfies Record<CheckTaskType, string>;
export function taskTypeDisplayValue(taskType: CheckTaskType | 'unknown') {
  return taskType === 'unknown' ? 'Tasks' : taskTypeDisplayValues[taskType];
}

function LegacyCardWrapper({ children }: { children: React.ReactNode }) {
  return (
    <Card
      isDivided={false}
      variant="unstyled"
      className="h-0 min-h-3/4 flex-1 overflow-y-auto overflow-x-hidden"
    >
      {children}
    </Card>
  );
}
export function CheckTasks() {
  const location = useLocation();
  const {
    currentCheckTasksRoute,
    currentBuildCheckDetailsRoute,
    currentDownstreamCheckDetailsRoute,
    currentLintCheckDetailsRoute,
    currentCustomCheckDetailsRoute,
    currentFilterBuildCheckDetailsRoute,
    currentProposalCheckDetailsRoute,
    currentOperationsCheckDetailsRoute,
  } = useInternalGraphLinking();
  const { taskType, checkId, graphId } = useRouteParams(currentCheckTasksRoute);

  const { data, loading, error } = useQuery<
    GraphQLTypes.CheckTasksQuery,
    GraphQLTypes.CheckTasksQueryVariables
  >(
    gql`
      query CheckTasksQuery($graphId: ID!, $checkId: ID!) {
        service(id: $graphId) {
          id
          checkWorkflow(id: $checkId) {
            id
            rerunOf {
              id
              ...RerunBannerCheckFragment
            }
            tasks {
              id
              ...TasksMenuTaskFragment
              ...MatchingProposalsDrawerFragment
            }
          }
        }
      }
      ${TasksMenuTaskFragment}
      ${MatchingProposalsDrawerFragment}
      ${RerunBannerCheckFragment}
    `,
    { variables: { graphId, checkId } },
  );
  const graph = data?.service;
  const workflow = graph?.checkWorkflow;
  const defaultTaskTab = useDefaultTaskType(taskType, workflow?.tasks);
  const proposalTask = workflow?.tasks.find(
    (
      task,
    ): task is Extract<typeof task, { __typename: 'ProposalsCheckTask' }> =>
      task.__typename === 'ProposalsCheckTask',
  );
  if (loading) {
    return <Loading />;
  } else if (error) {
    return <ErrorPage error={error} />;
  } else if (!workflow) {
    return <NotFound />;
  }

  if (!taskType && defaultTaskTab !== 'unknown') {
    return (
      <Navigate
        to={currentCheckTasksRoute.locationFrom({
          location,
          patch: { taskType: defaultTaskTab },
        })}
      />
    );
  }
  return (
    <DocumentTitle title="Tasks">
      <div className="flex h-full">
        <CheckSidebar>
          <TasksMenu defaultTaskTab={defaultTaskTab} tasks={workflow.tasks} />
        </CheckSidebar>
        <div className="flex w-0 flex-1 flex-col space-y-6 overflow-y-auto p-6">
          <RerunBanner
            graphId={graphId}
            checkId={checkId}
            rerunOfCheck={data?.service?.checkWorkflow?.rerunOf ?? null}
            className="flex-none"
          />
          <Switch>
            <Route
              path={currentCustomCheckDetailsRoute.definition}
              render={() => (
                <DocumentTitle title={taskTypeDisplayValue('custom')}>
                  <LegacyCardWrapper>
                    <CustomCheckDetails />
                  </LegacyCardWrapper>
                </DocumentTitle>
              )}
            />
            <Route
              path={currentLintCheckDetailsRoute.definition}
              render={() => (
                <DocumentTitle title={taskTypeDisplayValue('linter')}>
                  <LegacyCardWrapper>
                    <LintCheckDetails />
                  </LegacyCardWrapper>
                </DocumentTitle>
              )}
            />
            <Route
              path={currentBuildCheckDetailsRoute.definition}
              render={() => (
                <DocumentTitle title={taskTypeDisplayValue('build')}>
                  <LegacyCardWrapper>
                    <BuildCheckDetails />
                  </LegacyCardWrapper>
                </DocumentTitle>
              )}
            />
            <Route
              path={currentOperationsCheckDetailsRoute.definition}
              render={() => (
                <DocumentTitle title={taskTypeDisplayValue('operations')}>
                  <OperationCheckResults />
                </DocumentTitle>
              )}
            />
            <Route
              path={currentDownstreamCheckDetailsRoute.definition}
              render={() => (
                <DocumentTitle title={taskTypeDisplayValue('downstream')}>
                  <LegacyCardWrapper>
                    <DownstreamCheckDetails />
                  </LegacyCardWrapper>
                </DocumentTitle>
              )}
            />
            <Route
              path={currentFilterBuildCheckDetailsRoute.definition}
              render={() => (
                <DocumentTitle title={taskTypeDisplayValue('filter')}>
                  <LegacyCardWrapper>
                    <FilterBuildCheckDetails />
                  </LegacyCardWrapper>
                </DocumentTitle>
              )}
            />
            <Route
              path={currentProposalCheckDetailsRoute.definition}
              render={() => (
                <DocumentTitle title={taskTypeDisplayValue('proposals')}>
                  <LegacyCardWrapper>
                    <ProposalCheckDetails />
                  </LegacyCardWrapper>
                </DocumentTitle>
              )}
            />
            <Route component={NotFound} />
          </Switch>
        </div>
        {/* TODO: once we remove the card, this should be able to be moved inside the detail page */}
        {taskType === 'proposals' && proposalTask && (
          <MatchingProposalsDrawer
            workflowTask={proposalTask}
            className="max-xl:absolute max-xl:right-0 max-xl:top-0"
          />
        )}
      </div>
    </DocumentTitle>
  );
}
